// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useEffect, type ReactNode, type CSSProperties } from 'react';

import type { FileId } from '@evolvemep/api-ts';
import { Avatar, type AvatarProps, Loader, Card } from '@mantine/core';

import { useFileCache } from 'components/Mantine/FileUploads/useFileCache';

type Props = {
  title?: string;
  fileId: FileId | undefined | null;
  withContainer?: boolean;
  fallback?: ReactNode;
  objectFit?: CSSProperties['objectFit'];
} & AvatarProps;

export const usePresignedFile = (fileId: FileId | null | undefined) => {
  const { getPresignedUrl } = useFileCache();
  const [loading, setLoading] = useState(false);
  const [presignedUrl, setPresignedUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (fileId) {
      setLoading(true);
      void getPresignedUrl(fileId, false)
        .then((f) => setPresignedUrl(f?.preSignedUrl))
        .finally(() => setLoading(false));
    }
  }, [fileId, getPresignedUrl]);
  return [presignedUrl, loading] as const;
};

const Container = ({
  children,
  withContainer,
  size,
}: { children: ReactNode } & Pick<Props, 'size' | 'withContainer'>) =>
  withContainer ? (
    <Card
      p={0}
      w={size}
      h={size}
      miw={size}
      mih={size}
      style={{ overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {children}
    </Card>
  ) : (
    children
  );

export const ImageFromFile = ({
  fileId,
  fallback,
  objectFit = 'contain',
  withContainer,
  children,
  ...props
}: Props) => {
  const [presignedUrl, loading] = usePresignedFile(fileId);

  return (
    <Container size={props.size} withContainer={withContainer}>
      <Avatar
        radius="sm"
        src={presignedUrl}
        {...props}
        w="auto"
        h="auto"
        styles={{
          placeholder: { backgroundColor: 'white' },
          image: { objectFit },
          // TODO: `.style` can now be a function, need to handle it
          // oxlint-disable-next-line typescript/no-misused-spread
          ...props.styles,
        }}
      >
        {loading ? <Loader /> : fallback}
      </Avatar>
      {children}
    </Container>
  );
};
